import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { colors } from "utils";

/**
 * `onSoundDetected` and `onLevel` are captured once by the animation-frame
 * loop, so both must be stable references (see MicCheck's useCallback).
 * `onLevel` fires every frame with the same 0..1 value the bar is drawn from.
 */
export const MicMeter = ({ mediaStreamTrack, onSoundDetected, onLevel }) => {
  const [level, setLevel] = useState(0);
  const animFrameRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const soundDetectedRef = useRef(false);

  useEffect(() => {
    if (!mediaStreamTrack) {
      setLevel(0);
      return;
    }

    // Every new track deserves a fresh detection pass. Without this, swapping
    // from a working mic → broken mic → back to working leaves the button
    // stuck disabled because the ref latched true on the first detection and
    // never re-fires onSoundDetected for subsequent tracks.
    soundDetectedRef.current = false;

    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtx.resume().catch(() => {});
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.5;

    const stream = new MediaStream([mediaStreamTrack]);
    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);

    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      analyser.getByteFrequencyData(dataArray);
      const sum = dataArray.reduce((a, b) => a + b, 0);
      const avg = sum / dataArray.length;
      const normalized = Math.min(avg / 128, 1);
      setLevel(normalized);
      if (onLevel) onLevel(normalized);
      if (!soundDetectedRef.current && normalized > 0.05 && onSoundDetected) {
        soundDetectedRef.current = true;
        onSoundDetected();
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      source.disconnect();
      audioCtx.close();
    };
  }, [mediaStreamTrack]);

  return (
    <MeterWrapper>
      <MeterLabel>Speak to test Mic</MeterLabel>
      <MeterTrack>
        <MeterBg />
        <MeterFill style={{ width: `${level * 100}%` }} />
      </MeterTrack>
    </MeterWrapper>
  );
};

const MeterWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const MeterLabel = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;
`;

const MeterTrack = styled.div`
  position: relative;
  height: 4px;
  width: 100%;
`;

const MeterBg = styled.div`
  background-color: ${colors.gray700};
  border-radius: 100px;
  height: 4px;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const MeterFill = styled.div`
  background-color: ${colors.blue500};
  border-radius: 100px;
  height: 4px;
  position: absolute;
  top: 0;
  left: 0;
  transition: width 0.1s ease;
`;
